import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Package, ArrowUp, ArrowDown, Check } from 'lucide-react';
import LoadingSpinner from './LoadingSpinner';

const fmtNum = (v, digits = 0) => {
  if (v === null || v === undefined || isNaN(v) || !isFinite(v)) return '-';
  return new Intl.NumberFormat('id-ID', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  }).format(v);
};

const fmtInt = (v) => fmtNum(Math.round(v || 0), 0);

const computeKriteria = (modal, penjualanGr) => {
  // Modal kelipatan 10 milyar → threshold = kelipatan × 1.000.000 gr
  // 10M → 1jt gr, 20M → 2jt gr, 30M → 3jt gr, dst.
  const modalMultiple = Math.floor((modal || 0) / 10_000_000_000);
  const threshold = modalMultiple * 1_000_000;
  return (penjualanGr || 0) > threshold;
};

const KriteriaBadge = ({ up }) => (
  <span
    className={`inline-flex items-center justify-center w-6 h-6 rounded ${
      up ? 'bg-green-500' : 'bg-red-500'
    }`}
    data-testid={`kriteria-${up ? 'up' : 'down'}`}
    title={up ? 'Memenuhi kriteria' : 'Di bawah kriteria'}
  >
    {up ? (
      <ArrowUp size={14} className="text-white" strokeWidth={3} />
    ) : (
      <ArrowDown size={14} className="text-white" strokeWidth={3} />
    )}
  </span>
);

const computeLabaKriteria = (labaPerModal) => {
  // laba_per_modal is stored as ratio (e.g., 0.03 = 3%)
  const pct = (labaPerModal || 0) * 100;
  if (pct < 2) return 'down';
  if (pct <= 4) return 'check';
  return 'up';
};

const LabaKriteriaBadge = ({ status }) => {
  const config = {
    up: { bg: 'bg-green-500', Icon: ArrowUp, title: 'Sangat baik (> 4%)' },
    check: { bg: 'bg-emerald-500', Icon: Check, title: 'Baik (2% - 4%)' },
    down: { bg: 'bg-red-500', Icon: ArrowDown, title: 'Kurang (< 2%)' }
  };
  const { bg, Icon, title } = config[status] || config.down;
  return (
    <span
      className={`inline-flex items-center justify-center w-6 h-6 rounded ${bg}`}
      data-testid={`laba-kriteria-${status}`}
      title={title}
    >
      <Icon size={14} className="text-white" strokeWidth={3} />
    </span>
  );
};

const fmtRatio = (v) => {
  if (v === null || v === undefined || isNaN(v) || !isFinite(v)) return '-';
  return `${(v * 100).toFixed(2)}%`;
};

const YearlySummaryTable = ({
  selectedYear,
  yearlySummary,
  loadingYearlySummary,
  selectedPeriod
}) => {
  if (!selectedYear) return null;

  const totals = yearlySummary.reduce((acc, r) => {
    acc.modal += r.modal || 0;
    acc.penjualan_gr += r.penjualan_gr || 0;
    acc.biaya_pusat += r.biaya_pusat || 0;
    acc.laba_bersih += r.laba_bersih || 0;
    acc.jumlah_cabang_sum += r.jumlah_cabang || 0;
    acc.count += 1;
    return acc;
  }, { modal: 0, penjualan_gr: 0, biaya_pusat: 0, laba_bersih: 0, jumlah_cabang_sum: 0, count: 0 });

  const totalLabaPerGr = totals.penjualan_gr > 0 ? totals.laba_bersih / totals.penjualan_gr : null;
  const totalBiayaPerGr = totals.penjualan_gr > 0 ? totals.biaya_pusat / totals.penjualan_gr : null;
  const totalLabaPerModal = totals.modal > 0 ? totals.laba_bersih / totals.modal : null;
  const avgCabang = totals.count > 0 ? totals.jumlah_cabang_sum / totals.count : 0;

  return (
    <Card className="border-2 border-amber-200" data-testid="yearly-summary-card">
      <CardHeader className="bg-gradient-to-r from-amber-50 to-yellow-50">
        <CardTitle className="text-xl font-bold text-slate-800 flex items-center gap-2">
          <Package size={20} />
          History Penjualan - Tahun {selectedYear}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-6">
        {loadingYearlySummary ? (
          <LoadingSpinner size="sm" color="amber" />
        ) : yearlySummary.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            Tidak ada data periode untuk tahun {selectedYear}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm" data-testid="yearly-summary-table">
              <thead>
                <tr className="bg-amber-100 border-b-2 border-amber-600">
                  <th className="px-2 py-1.5 text-left text-xs font-bold text-slate-700">No.</th>
                  <th className="px-2 py-1.5 text-left text-xs font-bold text-slate-700">Bulan</th>
                  <th className="px-2 py-1.5 text-right text-xs font-bold text-slate-700">Modal</th>
                  <th className="px-2 py-1.5 text-right text-xs font-bold text-slate-700">Penjualan (gr)</th>
                  <th className="px-2 py-1.5 text-center text-xs font-bold text-slate-700">Kriteria</th>
                  <th className="px-2 py-1.5 text-right text-xs font-bold text-slate-700">Laba Bersih</th>
                  <th className="px-2 py-1.5 text-right text-xs font-bold text-slate-700">Laba / gr</th>
                  <th className="px-2 py-1.5 text-right text-xs font-bold text-slate-700">Biaya Pusat</th>
                  <th className="px-2 py-1.5 text-right text-xs font-bold text-slate-700">Biaya / gr</th>
                  <th className="px-2 py-1.5 text-center text-xs font-bold text-slate-700">Jumlah Cabang</th>
                  <th className="px-2 py-1.5 text-right text-xs font-bold text-slate-700">Laba / Modal</th>
                  <th className="px-2 py-1.5 text-center text-xs font-bold text-slate-700">Kriteria</th>
                </tr>
              </thead>
              <tbody>
                {yearlySummary.map((item, index) => (
                  <tr
                    key={`${item.period_id}-${item.month}`}
                    className={`border-b hover:bg-amber-50 ${index % 2 === 0 ? 'bg-white' : 'bg-amber-50/30'}`}
                    data-testid={`history-row-${item.month}`}
                  >
                    <td className="px-2 py-1 text-xs text-slate-700">{index + 1}</td>
                    <td className="px-2 py-1 text-xs text-slate-900">
                      {item.month_label}
                    </td>
                    <td className="px-2 py-1 text-xs text-right text-slate-900">
                      {fmtInt(item.modal)}
                    </td>
                    <td className="px-2 py-1 text-xs text-right text-slate-900">
                      {fmtInt(item.penjualan_gr)}
                    </td>
                    <td className="px-2 py-1 text-xs text-center">
                      <KriteriaBadge up={computeKriteria(item.modal, item.penjualan_gr)} />
                    </td>
                    <td className="px-2 py-1 text-xs text-right text-slate-900">
                      {fmtInt(item.laba_bersih)}
                    </td>
                    <td className="px-2 py-1 text-xs text-right text-slate-900">
                      {fmtInt(item.laba_per_gr)}
                    </td>
                    <td className="px-2 py-1 text-xs text-right text-slate-900">
                      {fmtInt(item.biaya_pusat)}
                    </td>
                    <td className="px-2 py-1 text-xs text-right text-slate-900">
                      {fmtInt(item.biaya_per_gr)}
                    </td>
                    <td className="px-2 py-1 text-xs text-center text-slate-900">
                      {item.jumlah_cabang ?? 0}
                    </td>
                    <td className="px-2 py-1 text-xs text-right text-slate-900">
                      {fmtRatio(item.laba_per_modal)}
                    </td>
                    <td className="px-2 py-1 text-xs text-center">
                      <LabaKriteriaBadge status={computeLabaKriteria(item.laba_per_modal)} />
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-amber-100 border-t-2 border-amber-600 font-bold">
                  <td colSpan="2" className="px-2 py-1.5 text-xs text-slate-800">TOTAL</td>
                  <td className="px-2 py-1.5 text-xs text-right text-slate-800">
                    {fmtInt(totals.modal)}
                  </td>
                  <td className="px-2 py-1.5 text-xs text-right text-slate-800">
                    {fmtInt(totals.penjualan_gr)}
                  </td>
                  <td className="px-2 py-1.5 text-xs text-center text-slate-500">—</td>
                  <td className="px-2 py-1.5 text-xs text-right text-slate-800">
                    {fmtInt(totals.laba_bersih)}
                  </td>
                  <td className="px-2 py-1.5 text-xs text-right text-slate-800">
                    {totalLabaPerGr === null ? '-' : fmtInt(totalLabaPerGr)}
                  </td>
                  <td className="px-2 py-1.5 text-xs text-right text-slate-800">
                    {fmtInt(totals.biaya_pusat)}
                  </td>
                  <td className="px-2 py-1.5 text-xs text-right text-slate-800">
                    {totalBiayaPerGr === null ? '-' : fmtInt(totalBiayaPerGr)}
                  </td>
                  <td className="px-2 py-1.5 text-xs text-center text-slate-800">
                    {fmtNum(avgCabang, 1)}
                  </td>
                  <td className="px-2 py-1.5 text-xs text-right text-slate-800">
                    {totalLabaPerModal === null ? '-' : fmtRatio(totalLabaPerModal)}
                  </td>
                  <td className="px-2 py-1.5 text-xs text-center">
                    {totalLabaPerModal === null ? <span className="text-slate-500">—</span> : (
                      <LabaKriteriaBadge status={computeLabaKriteria(totalLabaPerModal)} />
                    )}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default YearlySummaryTable;
